import track, { useTracking } from 'react-tracking';
import If from '@components/If/If';
import { TileLink } from '@/components/DeprecatedTile';
import { getGridViewCoverFromImageUrl } from '@/utilities/coverImages';
import {
  getCloudflareImageUrl,
  getResponsiveImage,
} from '@/hooks/useCloudflareImage';
import { Badge, Box, Text, Grid } from '@simplywallst/ui-core';
import { Tile } from '@/components/Tile';
import { Snowflake } from '@simplywallst/ui-charts/Snowflake';
import ScrollLoader from '@/components/ScrollLoader';
import { LoadingSkeleton } from './LoadingSkeleton';
import { useInfiniteInvestingIdeas } from '../../hooks/useInfiniteInvestingIdeas';
import { parseSnowflakePointsFromRules } from '@/pages/DiscoverPageV1/components/BasicHeading/components/NewBasicHeading/utils';
import { getInvestingIdeaPath } from './utils';
import { INVESTING_IDEAS_PAGE_SIZE } from './constants';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { CompanyLogo } from '@/components/CompanyLogo';

const LAZY_IDEA_START_INDEX = 6;

const ExploreIdeas = ({ market }: { market: PrimaryCountryISOElseGlobal }) => {
  const { trackEvent } = useTracking();

  const { data, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteInvestingIdeas({
      limit: INVESTING_IDEAS_PAGE_SIZE,
      market,
    });

  const investingIdeas = data ?? [];

  return (
    <>
      <If match={investingIdeas.length !== 0}>
        <ScrollLoader
          handleLoadRequest={() => {
            if (hasNextPage) {
              fetchNextPage();
            }
          }}
        >
          {() => {
            return (
              <Box
                marginLeft="x2"
                marginRight="x2"
                marginLeftLg="x0"
                marginRightLg="x0"
                marginBottom="x2"
              >
                <Grid
                  gap="24px"
                  templateColumnsMd="repeat(2, minmax(0, 1fr))"
                  templateColumnsLg="repeat(3, minmax(0, 1fr))"
                  width="100%"
                  data-cy-id="tile-view"
                >
                  {investingIdeas.map((idea, ideaIndex) => {
                    const { name, slug, imageUrl, resultPreview } = idea;
                    const topCompanies = resultPreview?.companies;
                    const totalNewCompanies =
                      resultPreview?.newCompaniesTotalHits ?? 0;
                    const companyCount = resultPreview?.totalHits ?? 0;
                    const snowflakeScores = parseSnowflakePointsFromRules(
                      resultPreview?.filterJson
                    );
                    const totalCompaniesIcon = topCompanies?.length ?? 0;
                    const totalCompanyCount = companyCount - totalCompaniesIcon;
                    const imageSrc = getCloudflareImageUrl({
                      src: getGridViewCoverFromImageUrl(imageUrl),
                      width: 1152,
                      height: 460,
                      fit: 'cover',
                    });

                    const responsive = getResponsiveImage({
                      src: getGridViewCoverFromImageUrl(imageUrl),
                      breakpoints: [
                        { width: 1152, height: 460, fit: 'cover' },
                        { width: 976, height: 368, fit: 'cover' },
                        { width: 768, height: 276, fit: 'cover' },
                        { width: 425, height: 197, fit: 'cover' },
                        { width: 375, height: 150, fit: 'cover' },
                        { width: 276, height: 110, fit: 'cover' },
                        { width: 163, height: 95, fit: 'cover' },
                      ],
                    });

                    return (
                      <TileLink
                        key={idea.id}
                        noUnderline
                        inheritColor
                        to={getInvestingIdeaPath({
                          id: idea.id,
                          market,
                          slug,
                          filterJson: idea.resultPreview?.filterJson,
                        })}
                        onClick={() => {
                          window.scrollTo({
                            top: 0,
                            left: 0,
                          });
                          trackEvent({
                            action: 'click',
                            subject: 'tile',
                            gridId: idea.id,
                            index: ideaIndex,
                          });
                        }}
                      >
                        <Tile>
                          <Tile.Header>
                            <Box height="143px" overflow="hidden">
                              <img
                                src={imageSrc}
                                srcSet={responsive.srcSet}
                                width="100%"
                                loading={
                                  ideaIndex >= LAZY_IDEA_START_INDEX
                                    ? 'lazy'
                                    : undefined
                                }
                              />
                            </Box>
                          </Tile.Header>
                          <Grid
                            height="326px"
                            paddingTop="x13"
                            paddingLeft="x3"
                            paddingRight="x3"
                            templateColumns="auto 120px"
                            backgroundColor="phantom"
                            gap="8px"
                            alignItems="center"
                          >
                            <Box>
                              <Text typography="largeStrong">{name}</Text>
                            </Box>
                            <Snowflake points={snowflakeScores}>
                              <Snowflake.Labels />
                            </Snowflake>
                          </Grid>
                          <Tile.Footer>
                            <Grid
                              templateColumns={`repeat(${totalCompaniesIcon}, 16px) max-content auto`}
                              gap="10px"
                              alignItems="center"
                              paddingBottom="x2"
                              paddingLeft="x3"
                            >
                              {topCompanies?.map((company) => {
                                return (
                                  <CompanyLogo
                                    key={`${company.id}`}
                                    website={company.info?.url ?? ''}
                                    size={20}
                                    hasLogo={company.info?.hasLogo ?? false}
                                    tickerSymbol={company.tickerSymbol ?? ''}
                                    isLazyLoaded={
                                      ideaIndex >= LAZY_IDEA_START_INDEX
                                    }
                                  />
                                );
                              })}
                              {totalCompanyCount > 0 && (
                                <Text typography="tiny">
                                  {`+${totalCompanyCount} companies`}
                                </Text>
                              )}
                              {totalNewCompanies > 0 && (
                                <Box>
                                  <Badge tone="attention">{`${totalNewCompanies} New`}</Badge>
                                </Box>
                              )}
                            </Grid>
                          </Tile.Footer>
                        </Tile>
                      </TileLink>
                    );
                  })}
                </Grid>
              </Box>
            );
          }}
        </ScrollLoader>
      </If>
      <If match={isFetching}>
        <LoadingSkeleton />
      </If>
    </>
  );
};

export default track(
  {
    type: 'track',
    subject: 'page',
    action: 'landed',
  },
  { dispatchOnMount: true }
)(ExploreIdeas) as typeof ExploreIdeas;
